import React, { useState, useEffect, useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import MultiFilter, { FilterOptions } from './components/MultiFilter'
import Button from '../components/Button'
import Icon from '../components/Icon'
import StatusBadge from '../components/StatusBadge'
import {
  AccommodationEvent,
  getAllAccommodationEvents,
} from '../../services/accommodationEvents'

const RawEventLogs: React.FC = () => {
  const [accommodationEvents, setAccommodationEvents] = useState<
    AccommodationEvent[]
  >([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterOptions>({
    classes: [],
    students: [],
    teachers: [],
    accommodations: [],
    dateRange: { startDate: '', endDate: '' },
  })

  // Fetch accommodation events on component mount
  useEffect(() => {
    fetchAccommodationEvents()
  }, [])

  const fetchAccommodationEvents = async () => {
    setLoading(true)
    setError(null)
    try {
      const events = await getAllAccommodationEvents()
      // Sort by date (newest first)
      const sortedEvents = events.sort((a, b) => {
        return new Date(b.date).getTime() - new Date(a.date).getTime()
      })
      setAccommodationEvents(sortedEvents)
    } catch (err: any) {
      console.error('Error fetching accommodation events:', err)
      setError(
        err.response?.data?.error || 'Failed to fetch accommodation events',
      )
    } finally {
      setLoading(false)
    }
  }

  // Filter accommodation events based on selected filters
  const filteredAccommodationEvents = useMemo(() => {
    return accommodationEvents.filter((event) => {
      // Filter by classes
      if (filters.classes.length > 0) {
        if (!event.class || !filters.classes.includes(event.class._id)) {
          return false
        }
      }

      // Filter by students
      if (filters.students.length > 0) {
        if (!event.student || !filters.students.includes(event.student._id)) {
          return false
        }
      }

      // Filter by teachers
      if (filters.teachers.length > 0) {
        if (!event.teacher || !filters.teachers.includes(event.teacher._id)) {
          return false
        }
      }

      // Filter by accommodations
      if (filters.accommodations.length > 0) {
        if (
          !event.accommodation ||
          !filters.accommodations.includes(event.accommodation)
        ) {
          return false
        }
      }

      // Filter by date range (using date field)
      if (filters.dateRange.startDate || filters.dateRange.endDate) {
        const eventDate = new Date(event.date)

        if (filters.dateRange.startDate) {
          const startDate = new Date(filters.dateRange.startDate)
          // Set start date to beginning of day to ensure proper comparison
          startDate.setHours(0, 0, 0, 0)
          if (eventDate < startDate) {
            return false
          }
        }

        if (filters.dateRange.endDate) {
          const endDate = new Date(filters.dateRange.endDate)
          // Set end date to end of day to include the entire end date
          endDate.setHours(23, 59, 59, 999)
          if (eventDate > endDate) {
            return false
          }
        }
      }

      return true
    })
  }, [accommodationEvents, filters])

  // Handle filter changes
  const handleFiltersChange = (newFilters: FilterOptions) => {
    setFilters(newFilters)
  }

  // Handle print functionality
  const handlePrint = () => {
    // Add a slight delay to ensure any state changes are rendered
    setTimeout(() => {
      window.print()
    }, 100)
  }

  // Generate print summary
  const printSummary = useMemo(() => {
    const currentDate = new Date().toLocaleDateString()
    const totalEvents = filteredAccommodationEvents.length
    const uniqueStudents = new Set(
      filteredAccommodationEvents.map((event) => event.student._id),
    ).size
    const uniqueClasses = new Set(
      filteredAccommodationEvents.map((event) => event.class._id),
    ).size

    return {
      date: currentDate,
      totalEvents,
      uniqueStudents,
      uniqueClasses,
      dateRange:
        filters.dateRange.startDate || filters.dateRange.endDate
          ? `${filters.dateRange.startDate || 'Beginning'} to ${filters.dateRange.endDate || 'Present'}`
          : 'All dates',
    }
  }, [filteredAccommodationEvents, filters])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Provided':
        return 'bg-green-100 text-green-800'
      case 'Declined':
        return 'bg-red-100 text-red-800'
      case 'Not Needed':
        return 'bg-blue-100 text-blue-800'
      case 'N/A':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<AccommodationEvent>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        cell: (info) => (
          <div className="text-sm font-medium text-gray-900">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        accessorKey: 'student',
        header: 'Student',
        cell: (info) => {
          const student = info.getValue() as AccommodationEvent['student']
          return (
            <div className="text-sm text-gray-900">
              {student.firstName} {student.lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'accommodation',
        header: 'Accommodation',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const status = info.getValue() as string

          return (
            <StatusBadge colorClass={getStatusColor(status)}>
              {status}
            </StatusBadge>
          )
        },
      },
      {
        accessorKey: 'teacher',
        header: 'Teacher',
        cell: (info) => {
          const teacher = info.getValue() as AccommodationEvent['teacher']
          return (
            <div className="text-sm text-gray-900">
              {teacher.firstName} {teacher.lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'class',
        header: 'Class',
        cell: (info) => {
          const classInfo = info.getValue() as AccommodationEvent['class']
          return <div className="text-sm text-gray-900">{classInfo.name}</div>
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Logged At',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDateTime(info.getValue() as string)}
          </div>
        ),
      },
    ],
    [],
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Print Styles */}
      <style>{`
        @media print {
          * { overflow: visible !important; }

          /* Hide non-essential elements */
          .no-print {
            display: none !important;
          }

          /* Show print-only elements */
          .print-only {
            display: block !important;
          }

          /* Ensure the main content is visible */
          body {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Print header styling */
          .print-header {
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }

          /* Force black text for better printing */
          .print-text {
            color: #000 !important;
          }

          /* Print summary styles */
          .print-summary {
            background: #f9f9f9 !important;
            border: 1px solid #ccc !important;
            padding: 15px !important;
            margin-bottom: 20px !important;
            page-break-after: avoid !important;
          }

          /* Print list styles */
          .print-event-list {
            page-break-inside: auto;
          }

          .print-event-item {
            border: 1px solid #ccc;
            padding: 10px;
            margin-bottom: 10px;
            background: white;
            page-break-inside: avoid;
          }

          /* Status styling for print */
          .status-provided { background-color: #dcfce7 !important; color: #166534 !important; }
          .status-declined { background-color: #fee2e2 !important; color: #991b1b !important; }
          .status-not-needed { background-color: #dbeafe !important; color: #1e40af !important; }
          .status-na { background-color: #f3f4f6 !important; color: #1f2937 !important; }
        }

        @page {
          margin: 0.5in;
          size: letter;
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 print-text">
            Raw Event Logs
          </h1>
          <p className="text-gray-600 print-text">
            View all accommodation events logged for your classes
          </p>
        </div>
        {/* Print Button */}
        <Button
          onClick={handlePrint}
          variant="secondary"
          className="ml-auto no-print"
          disabled={loading || filteredAccommodationEvents.length === 0}
        >
          <Icon type="printer" className="w-4 h-4 mr-2" />
          Print Logs
        </Button>
      </div>

      {/* Print-only Summary */}
      <div className="hidden print:block print-summary">
        <div className="print-header">
          <h2 className="text-xl font-bold print-text">
            Accommodation Event Logs Summary
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm print-text">
          <div>
            <strong>Report Date:</strong> {printSummary.date}
          </div>
          <div>
            <strong>Date Range:</strong> {printSummary.dateRange}
          </div>
          <div>
            <strong>Total Events:</strong> {printSummary.totalEvents}
          </div>
          <div>
            <strong>Unique Students:</strong> {printSummary.uniqueStudents}
          </div>
          <div>
            <strong>Unique Classes:</strong> {printSummary.uniqueClasses}
          </div>
        </div>
      </div>

      {/* Print-only Event List */}
      <div className="hidden print:block print-event-list">
        {filteredAccommodationEvents.map((event, index) => (
          <div key={event._id || index} className="print-event-item">
            <div className="grid grid-cols-2 gap-4 text-sm print-text">
              <div>
                <strong>Date:</strong> {formatDate(event.date)}
              </div>
              <div>
                <strong>Student:</strong> {event.student.firstName}{' '}
                {event.student.lastName}
              </div>
              <div>
                <strong>Teacher:</strong> {event.teacher.firstName}{' '}
                {event.teacher.lastName}
              </div>
              <div>
                <strong>Class:</strong> {event.class.name}
              </div>
              <div className="col-span-2">
                <strong>Accommodation:</strong> {event.accommodation}
              </div>
              <div>
                <strong>Status:</strong>
                <span
                  className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                    event.status === 'Provided'
                      ? 'status-provided'
                      : event.status === 'Declined'
                        ? 'status-declined'
                        : event.status === 'Not Needed'
                          ? 'status-not-needed'
                          : 'status-na'
                  }`}
                >
                  {event.status}
                </span>
              </div>
              <div>
                <strong>Logged At:</strong> {formatDateTime(event.createdAt)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg no-print">
          {error}
        </div>
      )}

      {/* Multi Filter Component - Hidden in print */}
      <div className="no-print">
        <MultiFilter
          accommodationEvents={accommodationEvents}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      </div>

      {/* Accommodation Events Table - Hidden in print */}
      <div className="no-print">
        <DataTable
          data={filteredAccommodationEvents}
          columns={columns}
          searchPlaceholder="Search accommodation events..."
          emptyStateTitle="No accommodation events found"
          emptyStateDescription="No accommodation events have been logged for your classes yet."
          emptyStateIcon={
            <Icon
              type="document-text"
              className="mx-auto h-12 w-12 text-gray-400"
            />
          }
          loading={loading}
        />
      </div>
    </div>
  )
}

export default RawEventLogs
